/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";
import { pretty } from "@vlinder-web/utils-react";
import { MASQ_REPORT } from "../graphql/getMasqReport.query";
import { GET_REPORT_STATUS } from "../graphql/getReportStatus.query";

const is = require("is_js");

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IMasqReportMethods extends IPluginMethodMap {
  getMasqReport(args: any): Promise<any>;
}

export class MasqReportService implements IAgentPlugin {
  readonly methods: IMasqReportMethods;
  private pollingTime = 10000;

  constructor() {
    this.methods = {
      getMasqReport: this.getMasqReport.bind(this),
    };
  }

  async getMasqReport(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    let filters = {};
    let fields = [];
    let costumeAttributeFilter = [];

    try {
      if (client) {
        if (args?.presentation) {
          filters = {
            presentation: {
              eq: args.presentation,
            },
          };

          this.pollingTime = 10000;
        }

        if (args?.section) {
          filters = {
            ...filters,
            section: {
              eq: args?.section,
            },
          };

          this.pollingTime = 5000;
        }

        if (args?.sectionOptType) {
          filters = {
            ...filters,
            items___costumeType: {
              eq: args?.sectionOptType,
            },
          };

          this.pollingTime = 5000;
        }

        if (args?.fields) {
          fields = args?.fields;
          this.pollingTime = 5000;
        }
        if (args?.costumeAttributeFilter) {
          costumeAttributeFilter = args?.costumeAttributeFilter;
          this.pollingTime = 5000;
        }

        const costumeDataRes = await client.query({
          query: MASQ_REPORT,
          variables: {
            filters: filters,
            fields: fields,
            costumeAttributeFilter: costumeAttributeFilter,
          },
          fetchPolicy: "network-only",
        });

        if (
          costumeDataRes?.data?.getDetailedCostumeData?.jobId &&
          is.existy(costumeDataRes?.data?.getDetailedCostumeData?.jobId)
        ) {
          return this.checkStatus(
            costumeDataRes?.data?.getDetailedCostumeData?.jobId
          );
        } else {
          throw new Error("Some unexpected error occurred");
        }
      }
    } catch (err: any) {
      console.error("getMasqReport error ", err);

      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private async checkStatus(jobId: string, retries = 100) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      const reportRes = await client.query({
        query: GET_REPORT_STATUS,
        variables: {
          jobId: jobId,
        },
        fetchPolicy: "network-only",
      });

      if (reportRes?.data?.getReportStatus?.message === "success") {
        return reportRes?.data?.getReportStatus;
      } else if (
        reportRes?.data?.getReportStatus?.message === "pending" ||
        reportRes?.data?.getReportStatus?.message === "PaymentNotComplete"
      ) {
        if (retries > 0) {
          return new Promise((resolve, reject) => {
            setTimeout(() => {
              this.checkStatus(jobId, retries - 1)
                .then(resolve)
                .catch(reject);
            }, this.pollingTime || 10000);
          });
        } else {
          throw new Error("Exceeded retry limit for checking status");
        }
      } else {
        throw new Error("Some unexpected error occurred");
      }
    } catch (err: any) {
      console.error("checkStatus error ", err);

      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }
}
